"use client";

import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  Tag,
  Table,
  Space,
  Card,
  message,
  Segmented,
  Tooltip,
  Spin,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "./Category.css";
import api from "../../../api";
import RejectionModal from "../Approval/RejectionModal";

function Category() {
  const [tags, setTags] = useState([]);
  const [categoryInput, setCategoryInput] = useState("");
  const [subcategoryInput, setSubcategoryInput] = useState("");
  const [subcategories, setSubcategories] = useState([]); // Array of {name, sub_fld_rf_id}
  const [editingCategory, setEditingCategory] = useState(null);
  const [toggleUser, setToggleUser] = useState("categories");
  const [openRejectionModal, setOpenRejectionModal] = useState(false);
  const [serviceName, setServiceName] = useState("");
  const [servicesList, setServicesList] = useState([]);
  const [deleteSubCategory, setDeleteSubCategory] = useState(false)
  const [subCategoryIndex, setSubCategoryIndex] = useState(null)
  const [subCategoryData, setSubCategoryData] = useState(null)
  const [deleteService, setDeleteService] = useState(false); // ✅ new
  const [serviceToDelete, setServiceToDelete] = useState(null); // ✅ new
  const [currentCategory, setCurrentCategory] = useState(null)
  const [messageApi, contextHolder] = message.useMessage();
  const [editingService, setEditingService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  // New separate loading states for tables
  const [categoriesTableLoading, setCategoriesTableLoading] = useState(false);
  const [servicesTableLoading, setServicesTableLoading] = useState(false);

  // CATEGORY TABLE COLUMNS
  const columns = [
    {
      title: "Company Category",
      dataIndex: "category",
      key: "category",
      render: (text) => <div className="category-text">{text}</div>,
    },
    {
      title: "Subcategories",
      dataIndex: "subcategories",
      key: "subcategories",
      render: (subs) => (
        <Space size={[8, 8]} wrap>
          {subs.map((s, i) => (
            <Tag key={i} className="subcategory-tag">
              {s.name || s}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size={8} className="category-actions">
          <Tooltip title="Edit Category">
            <Button
              icon={<EditOutlined />}
              size="small"
              style={{
                borderRadius: 6,
                borderColor: "#ccc",
                color: "#555",
                background: "white",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onClick={() => handleEditCategory(record)}
            >
              Edit
            </Button>
          </Tooltip>

          <Tooltip title="Delete Category">
            <Button
              icon={<DeleteOutlined />}
              size="small"
              style={{
                borderRadius: 6,
                borderColor: "#ccc",
                color: "#d9534f", // subtle red for delete
                background: "white",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onClick={() => {
                setOpenRejectionModal(true);
                setCurrentCategory(record);
              }}
            >
              Delete
            </Button>
          </Tooltip>
        </Space>

      ),
    },
  ];

  // SERVICE / PRODUCT TABLE COLUMNS
  const serviceColumns = [
    {
      title: "Service / Product Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size={8} className="service-actions">
          <Tooltip title="Edit Service">
            <Button
              icon={<EditOutlined />}
              size="small"
              style={{
                borderRadius: 6,
                borderColor: "#ccc",
                color: "#555",
                background: "white",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onClick={() => handleEditService(record)}
            >
              Edit
            </Button>
          </Tooltip>

          <Tooltip title="Delete Service">
            <Button
              icon={<DeleteOutlined />}
              size="small"
              style={{
                borderRadius: 6,
                borderColor: "#ccc",
                color: "#d9534f",
                background: "white",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
              onClick={() => {
                setDeleteService(true);
                setServiceToDelete(record);
                setOpenRejectionModal(true);
              }}
            >
              Delete
            </Button>

          </Tooltip>
        </Space>

      ),
    },
  ];



  // FETCH CATEGORIES
  const getCategoryList = async () => {
    setCategoriesTableLoading(true); // Start table loading
    try {
      const payload = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
      };
      const response = await api.getCategoryList(payload);

      if (response?.data?.status === "1") {
        const formattedData = response.data.category_data.map((item, index) => ({
          key: item.fld_category_id || index,
          category: item.fld_category_name,
          subcategories: item.sub_category.map((sub) => ({
            name: sub.fld_sub_category_name,
            sub_fld_rf_id: sub.sub_fld_rf_id || "", // FIXED: sub_fld_rf_id
          })),
          fld_rf_id: item.fld_rf_id || "", // FIXED: fld_rf_id
          fld_category_id: item.fld_category_id || "",
        }));
        setData(
          formattedData.sort(
            (a, b) => Number(b.fld_category_id) - Number(a.fld_category_id)
          )
        );
      }
    } catch (error) {
      console.error("Category fetch error:", error);
      messageApi.error("Failed to get category list");
    } finally {
      setCategoriesTableLoading(false); // Stop table loading
    }
  };

  // FETCH SERVICES / PRODUCTS
  const getServicesList = async () => {
    setServicesTableLoading(true); // Start table loading
    try {
      const payload = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
      };
      const response = await api.getServicesProducts(payload);
      if (response?.data?.status === "1") {
        // FIX: Changed from master_tbl_services_products to services_products_data
        const formatted = response.data.services_products_data.map((item, index) => ({
          key: item.fld_service_id || index,
          name: item.fld_service_name,
          fld_rf_id: item.fld_rf_id || "",
        }));
        setServicesList(
          formatted.sort(
            (a, b) => Number(b.key) - Number(a.key)
          )
        );
      } else {
        setServicesList([]);
      }
    } catch (err) {
      console.error("Services fetch error:", err);
      messageApi.error("Failed to fetch services/products");
      setServicesList([]);
    } finally {
      setServicesTableLoading(false); // Stop table loading
    }
  };

  const handleDeleteSubCategory = async (reason) => {
    // If subcategory doesn't exist in DB yet, remove locally
    if (!subCategoryData.sub_fld_rf_id) {
      handleRemoveSubcategory(subCategoryIndex);
      return;
    }

    // Otherwise, delete from backend
    try {
      const payload = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
        sub_fld_rf_id: subCategoryData?.sub_fld_rf_id,
        fld_reason_for_deletion: reason ? reason : ""
      };

      const response = await api.deleteSubCategory(payload);

      if (response?.data?.status === "1") {
        messageApi.success("Subcategory deleted successfully");
        getCategoryList()
        // Remove from UI after successful deletion
        const newSubcategories = subcategories.filter(
          (_, i) => i !== subCategoryIndex
        );
        setDeleteSubCategory(false)
        setSubCategoryData(null)
        setSubCategoryIndex(null)
        setOpenRejectionModal(false)
        setSubcategories(newSubcategories);
      } else {
        messageApi.error("Failed to delete subcategory");
      }
    } catch (err) {
      console.error("Delete Error:", err);
      messageApi.error("Failed to delete subcategory");
    }
  };

  const handleDeleteCategory = async (reason) => {
    try {
      const payload = {
        "synceddatetime": "2025-05-29 10:08:00",
        "FormCode": "201",
        "AppVersion": "1.0.0.0",
        "ApiKey": "kavin",
        "AppTypeNo": "3",
        "fld_rf_id": currentCategory?.fld_rf_id,
        "fld_category_id": currentCategory?.fld_category_id,
        "fld_reason_for_deletion": reason ? reason : "admin deleted this"
      }

      const response = await api.deleteCategory(payload);

      if (response?.data?.status === "1") {
        messageApi.success("Category deleted successfully");
        getCategoryList();
        setCurrentCategory(null)
        setCategoryInput("");
        setSubcategories([]);
        setEditingCategory(null);
        setSubcategoryInput("");
        setOpenRejectionModal(false)

      } else {
        messageApi.error(response?.data?.message || "Failed to delete Category");
      }
    } catch (err) {
      console.error("Delete Error:", err);
      messageApi.error("Failed to delete Category");
    }
  }

  // ADD/UPDATE SERVICE/PRODUCT
  const handleAddService = async () => {
    if (!serviceName.trim()) {
      messageApi.warning("Please enter service/product name");
      return;
    }

    const payload = {
      master_tbl_services_products: [
        {
          fld_rf_id: editingService?.fld_rf_id || "",
          fld_service_id: editingService ? editingService.key : String(Date.now()),
          fld_service_name: serviceName.trim(),
          fld_loggedin_user_id: "admin",
          fld_data_source: "M"
        },
      ],
      synceddatetime: new Date().toISOString().slice(0, 19).replace("T", " "),
      FormCode: "201",
      AppVersion: "1.0.0.0",
      ApiKey: "kavin",
      AppTypeNo: "3",
    };


    try {
      setLoading(true);
      const response = await api.createServiceProduct(payload);

      if (response?.data?.status === "1") {
        messageApi.success(editingService ? "Service updated successfully" : "Service added successfully");
        setServiceName("");
        setEditingService(null);
        getServicesList();
      } else {
        messageApi.error(response?.data?.message || "Failed to save service/product");
      }
    } catch (err) {
      console.error("Service API Error:", err);
      messageApi.error("Failed to save service/product");
    } finally {
      setLoading(false);
    }
  };

  // DELETE SERVICE/PRODUCT
  const handleDeleteService = async (reason) => {
    try {
      const payload = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
        fld_rf_id: serviceToDelete?.fld_rf_id,
        fld_reason_for_deletion: reason || "Admin deleted this service",
      };

      const response = await api.deleteServiceProduct(payload);

      if (response?.data?.status === "1") {
        messageApi.success("Service deleted successfully");
        setServiceName("");
        setEditingService(null);
        getServicesList();
      } else {
        messageApi.error(response?.data?.message || "Failed to delete service");
      }
    } catch (err) {
      console.error("Delete Error:", err);
      messageApi.error("Failed to delete service");
    } finally {
      setDeleteService(false);
      setServiceToDelete(null);
      setOpenRejectionModal(false);
    }
  };


  useEffect(() => {
    getCategoryList();
    getServicesList();
  }, []);

  // CATEGORY HANDLERS
  const handleAddSubcategory = () => {
    if (!subcategoryInput.trim()) return;
    if (subcategories.some((s) => s.name === subcategoryInput.trim())) {
      messageApi.warning("Subcategory already added");
      return;
    }
    setSubcategories([...subcategories, { name: subcategoryInput.trim(), sub_fld_rf_id: "" }]); // FIXED: sub_fld_rf_id
    setSubcategoryInput("");
  };

  const handleRemoveSubcategory = (index) => {
    const newSubcategories = [...subcategories];
    newSubcategories.splice(index, 1);
    setSubcategories(newSubcategories);
  };

  const handleUpdateSubcategory = (index, value) => {
    const newSubcategories = [...subcategories];
    newSubcategories[index] = { ...newSubcategories[index], name: value };
    setSubcategories(newSubcategories);
  };

  const handleEditCategory = (record) => {
    setEditingCategory(record);
    setCategoryInput(record.category);
    // Map subcategories with correct field names
    setSubcategories(record.subcategories.map(sub => ({
      name: sub.name || sub,
      sub_fld_rf_id: sub.sub_fld_rf_id || "" // FIXED: sub_fld_rf_id
    })));

    document.querySelector('.category-add-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  const handleCancelEdit = () => {
    setCategoryInput("");
    setSubcategories([]);
    setEditingCategory(null);
    setSubcategoryInput("");
  };

  const handleSaveCategory = async () => {
    if (!categoryInput.trim()) {
      messageApi.warning("Please enter a category name");
      return;
    }
    if (subcategories.length === 0) {
      messageApi.warning("Please add at least one subcategory");
      return;
    }

    // Check if any subcategory is empty
    const emptySubcategory = subcategories.find(sub => !sub.name.trim());
    if (emptySubcategory) {
      messageApi.warning("Please fill all subcategory fields");
      return;
    }

    const payload = {
      category: [
        {
          fld_rf_id: editingCategory?.fld_rf_id || "", // FIXED: fld_rf_id
          fld_category_id: editingCategory?.key || "",
          fld_category_name: categoryInput,
          fld_loggedin_user_id: "admin",
          fld_data_source: "M",
          sub_category: subcategories.map((sub, i) => ({
            sub_fld_rf_id: sub.sub_fld_rf_id || "", // FIXED: sub_fld_rf_id
            fld_sub_category_id: String(200 + i + 1),
            fld_sub_category_name: sub.name,
          })),
        },
      ],
      synceddatetime: new Date().toISOString().slice(0, 19).replace("T", " "),
      FormCode: "201",
      AppVersion: "1.0.0.0",
      ApiKey: "kavin",
      AppTypeNo: "3",
    };


    try {
      setLoading(true);
      const response = editingCategory
        ? await api.categoryCreation(payload)
        : await api.categoryCreation(payload);


      if (response?.data?.status === "1") {
        messageApi.success(
          editingCategory ? "Category updated successfully" : "Category added successfully"
        );
        handleCancelEdit();
        getCategoryList();
      } else {
        messageApi.error(response?.data?.message || "Failed");
      }
    } catch (error) {
      console.error("Category API Error:", error);
      messageApi.error("API request failed");
    } finally {
      setLoading(false);
    }
  };

  // SERVICES / PRODUCTS HANDLERS
  const handleEditService = (record) => {
    setEditingService(record);
    setServiceName(record.name);

    document.querySelector('.services-tab-content .category-add-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  const handleCancelServiceEdit = () => {
    setServiceName("");
    setEditingService(null);
  };

  return (
    <div className="category-container">
      {contextHolder}
      <Segmented
        options={[
          { label: "Categories", value: "categories" },
          { label: "Services and Products", value: "tag" },
        ]}
        value={toggleUser}
        onChange={setToggleUser}
        className="category-custom-segmented"
      />

      {toggleUser === "categories" ? (
        <div className="category-tab-content">
          <div className="category-add-section">
            <div className="category-section-title">
              {editingCategory ? "Edit Category and Subcategories" : "Add Category and Subcategories"}
            </div>
            <Card className="category-card">
              <div className="category-label">Category Name</div>
              <Space.Compact className="subcategory-input-space">
                <Input
                  placeholder="Type a Category"
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  allowClear
                />
              </Space.Compact>

              <div className="subcategory-input-section">
                <div className="category-label">
                  Subcategories
                </div>

                {/* Existing subcategories in input boxes for editing */}
                <div className="subcategory-map">
                  {subcategories.map((sub, index) => (
                    <Space key={index} className="subcategory-input-space" style={{ marginBottom: 8, width: '100%' }}>
                      <Input
                        placeholder="Enter subcategory"
                        value={sub.name}
                        onChange={(e) => handleUpdateSubcategory(index, e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <Button
                        type="primary"
                        danger
                        shape="circle"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setDeleteSubCategory(true)
                          setSubCategoryData(sub)
                          setSubCategoryIndex(index)
                          setOpenRejectionModal(true)
                        }}
                      />

                    </Space>
                  ))}


                  {/* Add new subcategory input */}
                  <Space className="subcategory-input-space">
                    <Input
                      placeholder="Enter new subcategory"
                      value={subcategoryInput}
                      onChange={(e) => setSubcategoryInput(e.target.value)}
                      onPressEnter={handleAddSubcategory}
                    />
                    <Button
                      type="primary"
                      shape="circle"
                      style={{ backgroundColor: '#0072B1' }}
                      icon={<PlusOutlined />}
                      onClick={handleAddSubcategory}
                    />
                  </Space>
                </div>
              </div>

              <div className="cancel-edit">
                {editingCategory && (
                  <Button onClick={handleCancelEdit}>
                    Cancel Edit
                  </Button>
                )}
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleSaveCategory}
                  style={{ backgroundColor: '#0072B1' }}
                  loading={loading}
                >
                  {editingCategory ? "Update Category" : "Add Category"}
                </Button>
              </div>
            </Card>
          </div>

          <div className="category-list-section">
            <div className="category-section-title-small">Categories List</div>
            <Spin spinning={categoriesTableLoading}>
              <Table
                columns={columns}
                dataSource={data}
                pagination={{
                  pageSizeOptions: ["5", "10"],
                  showSizeChanger: true,
                  defaultPageSize: 5,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
                bordered
                className="category-table"
                loading={false} // Set to false since we're using Spin wrapper
              />
            </Spin>
          </div>
        </div>
      ) : (
        <div className="services-tab-content">
          <div className="category-add-section">
            <div className="category-section-title">
              {editingService ? "Edit Service / Product" : "Add Service / Product"}
            </div>
            <Card className="category-card">
              <div className="category-label">Service or Product Name</div>
              <Space.Compact className="subcategory-input-space" style={{ width: '100%' }}>
                <Input
                  placeholder="Enter service/product name"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  allowClear
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{ backgroundColor: '#0072B1' }}
                  onClick={handleAddService}
                  loading={loading}
                >
                  {editingService ? "Update" : "Add"}
                </Button>
                {editingService && (
                  <Button onClick={handleCancelServiceEdit}>
                    Cancel
                  </Button>
                )}
              </Space.Compact>
            </Card>
          </div>

          <div className="category-list-section">
            <div className="category-section-title-small">Services and Products List</div>
            <Spin spinning={servicesTableLoading}>
              <Table
                columns={serviceColumns}
                dataSource={servicesList}
                bordered
                className="category-table"
                loading={false}
                pagination={{
                  pageSizeOptions: ["5", "10"],
                  showSizeChanger: true,
                  defaultPageSize: 5,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
              />

            </Spin>
          </div>
        </div>
      )}

      {openRejectionModal && (
        <RejectionModal
          title={
            deleteSubCategory
              ? "Delete Sub Category"
              : deleteService
                ? "Delete Service / Product"
                : "Delete Category"
          }
          content={
            deleteSubCategory
              ? "Are you sure you want to delete this Sub Category? You cannot undo this change."
              : deleteService
                ? "Are you sure you want to delete this Service / Product? You cannot undo this change."
                : "Are you sure you want to delete this Category along with its Sub Categories? You cannot undo this change."
          }
          buttonName='Delete'
          open={openRejectionModal}
          onClose={() => {
            setOpenRejectionModal(false);
            setDeleteSubCategory(false);
            setDeleteService(false);
            setSubCategoryData(null);
            setSubCategoryIndex(null);
            setServiceToDelete(null);
          }}
          onSubmit={(reason) => {
            if (deleteSubCategory) handleDeleteSubCategory(reason);
            else if (deleteService) handleDeleteService(reason);
            else handleDeleteCategory(reason);
          }}
        />
      )}


    </div>
  );
}

export default Category;